import { useState } from 'react';
import { CheckCircle2, Send } from 'lucide-react';
import { Alert, Button, Input, Select, Textarea } from '@dreamtree-org/twreact-ui';
import { serviceOptions } from '@site/data';
import { submitEnquiry } from '@api/public';

const empty = { name: '', company: '', phone: '', email: '', serviceInterest: '', location: '', message: '', website: '' };

function validate(values) {
  const errors = {};
  if (values.name.trim().length < 2) errors.name = 'Please enter your name.';
  if (!/^[+\d][\d\s-]{8,14}$/.test(values.phone.trim())) errors.phone = 'Enter a valid phone number.';
  if (values.email && !/^\S+@\S+\.\S+$/.test(values.email.trim())) errors.email = 'Enter a valid email address.';
  if (!values.serviceInterest) errors.serviceInterest = 'Choose what you need help with.';
  return errors;
}

export default function EnquiryForm({ title = 'Request a quote', description }) {
  const [values, setValues] = useState(empty);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');
  const [serverError, setServerError] = useState('');

  const set = (key) => (eventOrValue) => {
    const value = eventOrValue?.target ? eventOrValue.target.value : eventOrValue;
    setValues((v) => ({ ...v, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  async function onSubmit(event) {
    event.preventDefault();
    const found = validate(values);
    setErrors(found);
    if (Object.keys(found).length) return;

    setStatus('loading');
    setServerError('');
    try {
      await submitEnquiry({ ...values, name: values.name.trim(), phone: values.phone.trim() });
      setStatus('success');
      setValues(empty);
    } catch (err) {
      setServerError(err.message);
      setStatus('error');
    }
  }

  if (status === 'success') {
    return (
      <div className="rounded-3xl border border-success-200 bg-success-50 p-8 text-center" role="status">
        <CheckCircle2 className="mx-auto h-12 w-12 text-success-600" aria-hidden="true" />
        <h3 className="mt-4 text-3xl text-ink-900">Thank you — we have your enquiry</h3>
        <p className="mt-2 text-ink-600">Our team will call you back within 48 hours to discuss your requirement.</p>
        <Button variant="outline" className="mt-6" onClick={() => setStatus('idle')}>
          Send another enquiry
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate className="relative rounded-3xl border border-[var(--app-border)] bg-white p-6 shadow-sm sm:p-8">
      <h3 className="text-3xl text-ink-950">{title}</h3>
      {description && <p className="mt-2 text-sm text-ink-500">{description}</p>}

      {status === 'error' && (
        <div className="mt-5">
          <Alert variant="error">{serverError}</Alert>
        </div>
      )}

      {/* Honeypot: hidden from people, tempting to bots. */}
      <div className="absolute -left-[9999px] h-px w-px overflow-hidden" aria-hidden="true">
        <label htmlFor="website">Website</label>
        <input id="website" name="website" tabIndex={-1} autoComplete="off" value={values.website} onChange={set('website')} />
      </div>

      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        <Input label="Your name" name="name" autoComplete="name" required value={values.name} onChange={set('name')} error={errors.name} />
        <Input label="Company (optional)" name="company" autoComplete="organization" value={values.company} onChange={set('company')} />
        <Input label="Phone number" name="phone" type="tel" autoComplete="tel" required value={values.phone} onChange={set('phone')} error={errors.phone} />
        <Input label="Email (optional)" name="email" type="email" autoComplete="email" value={values.email} onChange={set('email')} error={errors.email} />
        <Select
          label="What do you need?"
          placeholder="Select a service"
          options={serviceOptions}
          value={values.serviceInterest}
          onChange={set('serviceInterest')}
          error={errors.serviceInterest}
        />
        <Input label="Site location (optional)" name="location" placeholder="e.g. Peenya, Bengaluru" value={values.location} onChange={set('location')} />
        <div className="sm:col-span-2">
          <Textarea label="Message (optional)" name="message" rows={4} placeholder="Type of building, area, current systems…" value={values.message} onChange={set('message')} />
        </div>
      </div>

      <Button type="submit" size="lg" fullWidth loading={status === 'loading'} rightIcon={<Send className="h-4 w-4" />} className="mt-6 !bg-primary-600 hover:!bg-primary-700">
        Submit enquiry
      </Button>
    </form>
  );
}
